import {
  Component,
  computed,
  EventEmitter,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, Subject, takeUntil } from 'rxjs';
import { SepBarChartComponent } from '../../../../../shared/components/sep-bar-chart/sep-bar-chart.component';
import { TranslocoModule } from '@jsverse/transloco';
import { LicenseCalculationsService } from '../../services/license-calculations.service';
import { EnrollmentNotificationService } from '../../../../../core/services/enrollment-notification.service';
import { TextButtonComponent } from '../../../../../shared/components/text-button/text-button.component';
import { InfoCardComponent } from '../../../../../shared/components/info-card/info-card.component';
import { CurrentUserState } from '../../../../../core/store/current-user/current-user.state';
import { Subscription } from '../../../../../core/store/current-user/models/CurrentUserUIModel';
import { Store } from '@ngxs/store';

@Component({
  selector: 'app-license-info-card',
  imports: [
    SepBarChartComponent,
    TranslocoModule,
    TextButtonComponent,
    InfoCardComponent,
  ],
  templateUrl: './license-info-card.component.html',
  styleUrl: './license-info-card.component.css',
})
export class LicenseInfoCardComponent implements OnInit, OnDestroy {
  private readonly licenseCalculationsService = inject(
    LicenseCalculationsService,
  );
  private readonly enrollmentNotificationService = inject(
    EnrollmentNotificationService,
  );
  private readonly store = inject(Store);

  readonly pendingLicenseAssignments = input(0);

  private readonly serverAvailableLicenseCount = toSignal(
    this.licenseCalculationsService.getAvailableLicensesCount(),
    { initialValue: 0 },
  );
  readonly totalLicensesCount = toSignal(
    this.licenseCalculationsService.getTotalLicensesCount(),
    { initialValue: 0 },
  );
  readonly availableLicensesCount = computed(() =>
    Math.max(
      0,
      this.serverAvailableLicenseCount() - this.pendingLicenseAssignments(),
    ),
  );
  readonly availableToTotalLicensesRatio = computed(() => {
    const total = this.totalLicensesCount();
    return total > 0 ? (this.availableLicensesCount() / total) * 100 : 0;
  });

  @Input() type: 'basic' | 'enrollment' = 'basic';
  @Input() enrollmentDescription?: string;
  @Input() showLicenseRatioCard: boolean;
  @Input() hideAddLicensesButton = false;
  @Output() addLicensesButtonClickEvent: EventEmitter<void> =
    new EventEmitter<void>();

  subscription$: Observable<Subscription | null> = this.store.select(
    CurrentUserState.subscription,
  );
  freeTrial: boolean;
  private readonly destroy$: Subject<void> = new Subject();

  ngOnInit(): void {
    this.observeEnrollments();
    this.subscription$
      .pipe(takeUntil(this.destroy$))
      .subscribe((subscription) => {
        this.freeTrial = subscription?.freeTrialing ?? false;
      });
  }

  private observeEnrollments() {
    this.enrollmentNotificationService
      .getEnrollmentChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe((enrollmentChanged) => {
        if (enrollmentChanged) {
          this.licenseCalculationsService.refreshLicenses();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
